/*!
 * Cardio Online — Collection Faceted Filter Behaviour (v1.0)
 *
 * Adds best-practice UX on top of the Flex theme storefront-filter markup:
 *   - AJAX update (no full page reload) with debounced auto-apply (300ms)
 *   - URL sync via history.pushState (deep-linkable, back-button friendly)
 *   - Accordion accessibility (aria-expanded / aria-controls)
 *   - Mobile drawer (bottom sheet) with focus trap + ESC + body-scroll lock
 *   - Live "Apply (N products)" count preview while user toggles options
 *   - Active filter chip removal (delegated handler on .cos-active-chips)
 *   - Skeleton loading state on the product grid
 *
 * Designed to coexist with z__jsSidebar.js / z__jsCollection.js — it intercepts
 * the form change event BEFORE the legacy handler triggers location.search, so
 * the legacy full-reload path is bypassed.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;
  var GRID_SELECTOR = '[data-collection-main]';
  var FORM_SELECTOR = '[data-faceted-filter-form]';
  var CHIPS_SELECTOR = '[data-cos-active-chips]';
  var TOOLBAR_COUNT_SELECTOR = '[data-cos-result-count]';
  var DRAWER_SELECTOR = '[data-cos-drawer]';
  var DRAWER_BODY_SELECTOR = '[data-cos-drawer-body]';
  var DRAWER_TRIGGER_SELECTOR = '[data-cos-drawer-trigger]';
  var DRAWER_BADGE_SELECTOR = '[data-cos-drawer-badge]';
  var SORT_SELECTOR = '[data-cos-sort]';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function getForm() { return document.querySelector(FORM_SELECTOR); }
  function getGrid() { return document.querySelector(GRID_SELECTOR); }

  function buildUrlFromForm(form) {
    var fd = new FormData(form);
    var fp = new URLSearchParams(fd);
    var existing = new URLSearchParams(window.location.search);
    var out = new URLSearchParams();
    // 1) copy filter params from the form
    fp.forEach(function (v, k) { if (v !== '') out.append(k, v); });
    // 2) preserve non-filter params (sort_by, page, etc.)
    existing.forEach(function (v, k) {
      if (k.indexOf('filter.') !== 0) out.append(k, v);
    });
    return window.location.pathname + '?' + out.toString();
  }

  function applyUrl(url) { fetchAndSwap(url, true); }

  // -------------------------------------------------------------------------
  // AJAX fetch + DOM swap
  // -------------------------------------------------------------------------
  var inflight = null;

  function fetchAndSwap(url, pushHistory) {
    var grid = getGrid();
    if (!grid) { window.location.assign(url); return; }

    // Loading state
    grid.classList.add('is-cos-loading');

    if (inflight) inflight.abort();
    if (window.AbortController) inflight = new AbortController();
    var signal = inflight ? inflight.signal : undefined;

    fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: signal,
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Swap product grid
        var newGrid = doc.querySelector(GRID_SELECTOR);
        if (newGrid) {
          var oldMatrix = grid.querySelector('.collection-matrix__wrapper');
          var newMatrix = newGrid.querySelector('.collection-matrix__wrapper');
          if (oldMatrix && newMatrix) oldMatrix.parentNode.replaceChild(newMatrix, oldMatrix);
          var oldPagination = grid.querySelector('.container--pagination');
          var newPagination = newGrid.querySelector('.container--pagination');
          if (oldPagination && newPagination) oldPagination.parentNode.replaceChild(newPagination, oldPagination);
        }

        // Swap sidebar (filter form) so counts/active states refresh
        var oldForm = document.querySelector(FORM_SELECTOR);
        var newForm = doc.querySelector(FORM_SELECTOR);
        if (oldForm && newForm) {
          // Preserve scroll positions of any open accordion panels
          oldForm.parentNode.replaceChild(newForm, oldForm);
          // Re-run a11y hookup on the new DOM
          initAccordions();
          syncDrawerWithSidebar();
        }

        // Swap chip strip + result count
        ['[data-cos-active-chips]', '[data-cos-result-count]', '[data-cos-drawer-badge]'].forEach(function (sel) {
          var oldEl = document.querySelector(sel);
          var newEl = doc.querySelector(sel);
          if (oldEl && newEl) oldEl.parentNode.replaceChild(newEl, oldEl);
        });

        // Sync sort dropdown to match the navigated URL so it shows the active sort
        var sortEl = document.querySelector(SORT_SELECTOR);
        if (sortEl) {
          var sortParam = new URLSearchParams(url.split('?')[1] || '').get('sort_by');
          if (sortParam) sortEl.value = sortParam;
        }

        if (pushHistory) {
          window.history.pushState({ cos: true }, '', url);
        }

        // Inform other scripts (e.g. lazy load, reviews) that DOM changed
        document.dispatchEvent(new CustomEvent('cos:filtered', { detail: { url: url } }));
        if (window.PXUTheme && typeof window.PXUTheme.refreshTheme === 'function') {
          try { window.PXUTheme.refreshTheme(); } catch (e) { /* noop */ }
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return; // superseded by newer fetch
        // Fall back to a normal navigation on failure
        window.location.assign(url);
      })
      .then(function () {
        var g = getGrid();
        if (g) g.classList.remove('is-cos-loading');
      });
  }

  // -------------------------------------------------------------------------
  // Form change → debounced fetch
  // -------------------------------------------------------------------------
  function onFormChange(e) {
    // Always intercept — don't let the legacy z__jsSidebar handler navigate
    e.stopImmediatePropagation();
    var form = getForm();
    if (!form) return;
    var url = buildUrlFromForm(form);
    applyUrl(url);
  }
  var onFormChangeDebounced = debounce(onFormChange, DEBOUNCE_MS);

  function bindForm() {
    var form = getForm();
    if (!form) return;
    // Capture phase + stopImmediatePropagation pre-empts the legacy listener
    form.addEventListener(
      'change',
      function (e) { onFormChangeDebounced.call(form, e); },
      true
    );
    // Also intercept the per-group submit buttons (we've hidden them but defensively)
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      onFormChange.call(form, e);
    });
  }

  // -------------------------------------------------------------------------
  // Sort select (intercept full reload)
  // -------------------------------------------------------------------------
  function bindSort() {
    var sort = document.querySelector(SORT_SELECTOR);
    if (!sort) return;
    sort.addEventListener('change', function (e) {
      // Stop the legacy z__jsCollection.js handler from also firing — it does
      // $collectionMain.empty() which wipes the entire toolbar including this select.
      e.stopImmediatePropagation();
      var existing = new URLSearchParams(window.location.search);
      existing.set('sort_by', sort.value);
      var url = window.location.pathname + '?' + existing.toString();
      applyUrl(url);
    }, true);
  }

  // -------------------------------------------------------------------------
  // Accordion (a11y)
  // -------------------------------------------------------------------------
  function initAccordions() {
    var toggles = document.querySelectorAll('.cos-facet__toggle');
    toggles.forEach(function (btn) {
      if (btn.dataset.cosBound === '1') return;
      btn.dataset.cosBound = '1';
      // Make sure parent .cos-facet reflects the initial server-rendered aria state
      var parent = btn.closest('.cos-facet');
      if (parent) {
        if (btn.getAttribute('aria-expanded') === 'true') parent.classList.add('is-open');
        else parent.classList.remove('is-open');
      }
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var p = btn.closest('.cos-facet');
        if (p) p.classList.toggle('is-open', !expanded);
      });
    });
  }

  // -------------------------------------------------------------------------
  // Per-facet option search (in-list filter)
  // -------------------------------------------------------------------------
  function initOptionSearch() {
    document.querySelectorAll('.cos-facet__search').forEach(function (input) {
      if (input.dataset.cosBound === '1') return;
      input.dataset.cosBound = '1';
      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        var list = input.closest('.cos-facet__panel-inner').querySelector('.cos-facet__list');
        if (!list) return;
        list.querySelectorAll('.cos-facet__option').forEach(function (li) {
          var label = li.querySelector('.cos-facet__option-text');
          var text = label ? label.textContent.toLowerCase() : '';
          li.style.display = (!q || text.indexOf(q) > -1) ? '' : 'none';
        });
      });
    });
  }

  // -------------------------------------------------------------------------
  // Mobile drawer
  // -------------------------------------------------------------------------
  var lastFocus = null;

  function openDrawer() {
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var trigger = document.querySelector(DRAWER_TRIGGER_SELECTOR);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    // Move focus into the drawer
    var closeBtn = drawer.querySelector('[data-cos-drawer-close]');
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', onDrawerKey);
  }

  function closeDrawer() {
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    var trigger = document.querySelector(DRAWER_TRIGGER_SELECTOR);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    document.removeEventListener('keydown', onDrawerKey);
  }

  function onDrawerKey(e) {
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    var focusables = drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function bindDrawer() {
    var trigger = document.querySelector(DRAWER_TRIGGER_SELECTOR);
    if (trigger) trigger.addEventListener('click', function () {
      var open = trigger.getAttribute('aria-expanded') === 'true';
      open ? closeDrawer() : openDrawer();
    });
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    drawer.querySelectorAll('[data-cos-drawer-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });
    var overlay = drawer.querySelector('.cos-drawer__overlay');
    if (overlay) overlay.addEventListener('click', closeDrawer);
  }

  // On mobile, teleport the sidebar form into the drawer body so a single
  // source of truth drives both desktop sidebar and mobile drawer.
  function syncDrawerWithSidebar() {
    var drawerBody = document.querySelector(DRAWER_BODY_SELECTOR);
    var form = getForm();
    if (!drawerBody || !form) return;
    // Skip if already inside the drawer
    if (drawerBody.contains(form)) return;
    var mq = window.matchMedia('(max-width: 798px)');
    if (mq.matches) {
      drawerBody.appendChild(form);
    }
  }

  // -------------------------------------------------------------------------
  // Active chips removal (delegated)
  // -------------------------------------------------------------------------
  function bindChips() {
    document.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-cos-chip-remove]');
      if (!chip) return;
      e.preventDefault();
      applyUrl(chip.getAttribute('href'));
    });
  }

  // -------------------------------------------------------------------------
  // Back/forward (popstate) — re-fetch from URL
  // -------------------------------------------------------------------------
  function bindHistory() {
    window.addEventListener('popstate', function () {
      fetchAndSwap(window.location.pathname + window.location.search, false);
    });
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------
  // Measure the actual sticky/fixed site header on mobile so the toolbar
  // sits flush under it instead of disappearing behind it.
  function setMobileHeaderOffset() {
    var docEl = document.documentElement;
    if (window.innerWidth >= 799) {
      docEl.style.removeProperty('--cos-mobile-sticky-offset');
      return;
    }
    // Find the visible header bar height. Use specific inner-bar selectors first
    // — outer wrappers can include hidden mega-menus that expand the bounding box.
    // Each candidate must be visible AND under 140px tall (heuristic for a real header).
    var sel = [
      '.header__inner-content',  // Flex inner bar (z-index 1002) — usually the right one
      '.header__icons',
      '.header-section .header__wrapper',
      '.header__wrapper',
      '.site-header',
      '.header-section'
    ];
    var chosen = 0;
    for (var i = 0; i < sel.length; i++) {
      var el = document.querySelector(sel[i]);
      if (!el) continue;
      var r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;     // not visible
      if (r.height > 140) continue;                       // too tall — likely an expanded menu wrapper
      // bottom edge of this element in viewport coords; for a fixed/sticky header at top, this is the offset we want
      var bottom = r.bottom;
      if (bottom > 0 && bottom <= 140) { chosen = bottom; break; }
    }
    if (!chosen) chosen = 76; // sensible default for Flex mobile header
    var px = Math.max(0, Math.floor(chosen));
    docEl.style.setProperty('--cos-mobile-sticky-offset', px + 'px');
  }

  function init() {
    bindForm();
    bindSort();
    bindDrawer();
    bindChips();
    bindHistory();
    initAccordions();
    initOptionSearch();
    syncDrawerWithSidebar();
    setMobileHeaderOffset();
    window.addEventListener('resize', debounce(setMobileHeaderOffset, 150));
    window.addEventListener('scroll', debounce(setMobileHeaderOffset, 250), { passive: true });
    window.addEventListener('resize', debounce(function () {
      // If user resizes from mobile to desktop while drawer is open, close it
      var drawer = document.querySelector(DRAWER_SELECTOR);
      if (drawer && drawer.getAttribute('aria-hidden') === 'false' && window.innerWidth >= 799) {
        closeDrawer();
      }
      // Reset sidebar location when crossing the breakpoint
      var form = getForm();
      var drawerBody = document.querySelector(DRAWER_BODY_SELECTOR);
      var sidebar = document.querySelector('.sidebar-section aside, .sidebar-section');
      if (window.innerWidth >= 799 && drawerBody && drawerBody.contains(form) && sidebar) {
        sidebar.appendChild(form);
      } else if (window.innerWidth < 799) {
        syncDrawerWithSidebar();
      }
    }, 200));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
